import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  BlockStack, Box, Button, Collapsible, InlineStack, Text, TextField,
} from '@shopify/polaris';
import { APPS } from './api';
import { forInput, fromInput } from './useEditorForm';

const SWITCH_CSS = `
.star-switch { width: 38px; height: 22px; border-radius: 11px; border: 0; padding: 2px; cursor: pointer;
  background: var(--p-color-bg-fill-tertiary); transition: background .15s ease; flex: none; }
.star-switch[aria-checked="true"] { background: var(--p-color-bg-fill-success); }
.star-switch:focus-visible { outline: 2px solid var(--p-color-border-focus); outline-offset: 2px; }
.star-switch > span { display: block; width: 18px; height: 18px; border-radius: 50%; background: #fff;
  transform: translateX(0); transition: transform .15s ease; box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.star-switch[aria-checked="true"] > span { transform: translateX(16px); }
`;

const formatDate = (value) => (value
  ? new Date(value).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  })
  : null);

// Spelled out, because an empty start means "when it is published", not "now".
const runs = (window) => {
  const from = formatDate(window.starts_at) || 'when published';
  const to = formatDate(window.ends_at);

  return to ? `${from} → ${to}` : `${from}, no end date`;
};

const AppPicker = ({ codes, overrides = {}, error, onChange, onOverridesChange }) => {
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState({ starts_at: '', ends_at: '' });

  const beginEditing = (code) => {
    const own = overrides[code] || {};

    // Turning an app on is taken to mean "from now".
    setDraft({
      starts_at: own.starts_at || (codes.includes(code) ? '' : new Date().toISOString()),
      ends_at: own.ends_at || '',
    });
    setOpen(code);
  };

  const turnOff = (code) => {
    const { [code]: dropped, ...rest } = overrides;

    onOverridesChange(rest);
    onChange(codes.filter((c) => c !== code));
    if (open === code) setOpen(null);
  };

  const confirm = (code) => {
    onOverridesChange({
      ...overrides,
      // An empty start means now, not "no start at all".
      [code]: { starts_at: draft.starts_at || new Date().toISOString(), ends_at: draft.ends_at },
    });

    if (!codes.includes(code)) onChange([...codes, code]);
    setOpen(null);
  };

  const ready = !draft.starts_at || !draft.ends_at
    || new Date(draft.ends_at) > new Date(draft.starts_at);

  return (
    <BlockStack gap="300">
      <style>{SWITCH_CSS}</style>

      <Text as="span" variant="bodyMd" fontWeight="medium">Which apps show it</Text>

      <Text as="span" variant="bodySm" tone={codes.length && !error ? 'subdued' : 'critical'}>
        {error || (codes.length
          ? `Showing in ${codes.length} of ${APPS.length} apps. Order is set in the order view.`
          : 'Not shown anywhere yet. Turn on at least one app before publishing.')}
      </Text>

      <BlockStack gap="200">
        {APPS.map(({ code, name }) => {
          const on = codes.includes(code);
          const own = overrides[code] || {};

          return (
            <Box
              key={code}
              background={on ? 'bg-surface' : 'bg-surface-secondary'}
              borderWidth="025"
              borderColor="border"
              borderRadius="200"
              padding="300"
            >
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
                  <BlockStack gap="050">
                    <Text as="span" fontWeight={on ? 'semibold' : 'regular'}>{name}</Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {on ? runs(own) : 'Not shown here'}
                    </Text>
                  </BlockStack>

                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    {on && open !== code && (
                      <Button size="micro" onClick={() => beginEditing(code)}>Change dates</Button>
                    )}

                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`Show in ${name}`}
                      className="star-switch"
                      onClick={() => (on ? turnOff(code) : beginEditing(code))}
                    >
                      <span />
                    </button>
                  </InlineStack>
                </InlineStack>

                <Collapsible
                  id={`schedule-${code}`}
                  open={open === code}
                  transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}
                >
                  <Box paddingBlockStart="200" borderBlockStartWidth="025" borderColor="border">
                    <BlockStack gap="200">
                      <Text as="span" variant="bodySm" fontWeight="medium">
                        {`When should it run in ${name}?`}
                      </Text>

                      <Text as="span" variant="bodySm" tone="subdued">
                        These dates apply to this app only. Every other app keeps its own.
                        {' '}
                        Enter times in UTC — that is how they are stored and compared.
                      </Text>

                      <InlineStack gap="300" wrap>
                        <TextField
                          label="Starts (UTC)"
                          type="datetime-local"
                          autoComplete="off"
                          helpText="Set to now, in UTC. Change it to start later."
                          value={forInput(draft.starts_at)}
                          onChange={(value) => setDraft({ ...draft, starts_at: fromInput(value) })}
                        />
                        <TextField
                          label="Ends (UTC)"
                          type="datetime-local"
                          autoComplete="off"
                          helpText="Empty means it runs here until you stop it"
                          value={forInput(draft.ends_at)}
                          onChange={(value) => setDraft({ ...draft, ends_at: fromInput(value) })}
                        />
                      </InlineStack>

                      <InlineStack gap="200">
                        <Button variant="primary" disabled={!ready} onClick={() => confirm(code)}>
                          {on ? 'Save dates' : `Show in ${name}`}
                        </Button>
                        <Button variant="plain" onClick={() => setOpen(null)}>Cancel</Button>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </Collapsible>
              </BlockStack>
            </Box>
          );
        })}
      </BlockStack>
    </BlockStack>
  );
};

AppPicker.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.string).isRequired,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onOverridesChange: PropTypes.func.isRequired,
  overrides: PropTypes.object,
};

export default AppPicker;
